import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/projects', label: 'Projects' },
  { to: '/about', label: 'About' },
  { to: '/resources', label: 'Resources' },
  { to: '/contact', label: 'Contact' },
];

const PublicNav = () => {
  const { pathname } = useLocation();

  return (
    <nav className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-white/70 border-b border-white/40 shadow-sm" data-testid="public-nav">
      {/* Top micro-bar */}
      <div className="hidden xl:block border-b border-slate-100/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-end gap-4 h-7 text-[10px] text-slate-600">
          <span>Sydney &amp; Melbourne</span>
          <span className="text-slate-300">|</span>
          <a href="tel:0431469974" className="hover:text-[#0A2540] transition-colors font-medium">0431 469 974</a>
          <span className="text-slate-300">|</span>
          <a href="mailto:inquiry@plangenerator.com.au" className="hover:text-[#0A2540] transition-colors">inquiry@plangenerator.com.au</a>
        </div>
      </div>
      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-14">
        <Link to="/" className="text-2xl font-bold tracking-tight text-[#0A2540]" data-testid="nav-logo">
          PlanGen<span className="text-blue-600">Consulting</span>
        </Link>
        <div className="hidden md:flex items-center gap-7 text-base">
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`transition-colors ${pathname === to ? 'text-[#0A2540] font-semibold' : 'text-slate-500 hover:text-[#0A2540]'}`}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              {label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/generator"
            className="hidden lg:inline-flex px-5 py-1.5 rounded-full border border-blue-200 text-blue-600 text-sm font-semibold hover:bg-blue-50 transition-colors"
            data-testid="nav-generator-btn"
          >
            Generator
          </Link>
          <Link
            to="/contact"
            className="hidden lg:inline-flex px-5 py-1.5 rounded-full border border-amber-200 text-amber-700 text-sm font-semibold hover:bg-amber-50 transition-colors"
            data-testid="nav-quote-btn"
          >
            Request a Quote
          </Link>
          <Link
            to="/login"
            className="px-5 py-1.5 rounded-full bg-[#0A2540] text-white text-sm font-medium hover:bg-black transition-colors"
            data-testid="nav-login-btn"
          >
            Client Login
          </Link>
        </div>
      </div>
    </nav>
  );
};

const PublicFooter = () => (
  <footer className="bg-[#0A2540] border-t border-white/10 py-10">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid sm:grid-cols-4 gap-8 mb-8">
        <div>
          <span className="text-sm font-bold text-white">PlanGen<span className="text-blue-400">Consulting</span></span>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">Specialist waste management plan consultants for Australian property developments. Sydney &amp; Melbourne.</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Services</p>
          <div className="space-y-1.5">
            <Link to="/services" className="block text-xs text-slate-500 hover:text-white transition-colors">Operational WMP</Link>
            <Link to="/services" className="block text-xs text-slate-500 hover:text-white transition-colors">Demolition &amp; Construction WMP</Link>
            <Link to="/services" className="block text-xs text-slate-500 hover:text-white transition-colors">Stormwater Management</Link>
            <Link to="/services" className="block text-xs text-slate-500 hover:text-white transition-colors">HAZMAT Surveys</Link>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Resources</p>
          <div className="space-y-1.5">
            <Link to="/resources" className="block text-xs text-slate-500 hover:text-white transition-colors">Guides &amp; Insights</Link>
            <Link to="/projects" className="block text-xs text-slate-500 hover:text-white transition-colors">Projects</Link>
            <Link to="/about" className="block text-xs text-slate-500 hover:text-white transition-colors">About</Link>
            <Link to="/what-is-a-waste-management-plan" className="block text-xs text-slate-500 hover:text-white transition-colors">What is a WMP?</Link>
            <Link to="/waste-management-plan-nsw" className="block text-xs text-slate-500 hover:text-white transition-colors">WMP for NSW</Link>
            <Link to="/waste-management-plan-vic" className="block text-xs text-slate-500 hover:text-white transition-colors">WMP for VIC</Link>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Contact</p>
          <div className="space-y-3">
            <p className="text-[11px] text-slate-500 leading-relaxed">Sydney &amp; Melbourne<br />Servicing NSW &amp; VIC</p>
            <div className="pt-1 space-y-1">
              <p className="text-[10px] text-slate-600">ABN 922 849 40596</p>
              <a href="tel:0431469974" className="block text-[11px] text-slate-500 hover:text-white transition-colors">T 0431 469 974</a>
              <a href="mailto:inquiry@plangenerator.com.au" className="block text-[11px] text-slate-500 hover:text-white transition-colors">E inquiry@plangenerator.com.au</a>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 pt-6 text-center text-xs text-slate-500">
        &copy; {new Date().getFullYear()} PlanGen Consulting. All rights reserved. Professional Indemnity &amp; Public Liability Insured.
      </div>
    </div>
  </footer>
);

/* Mobile sticky CTA bar — click-to-call + Request a Quote */
const MobileCTA = () => (
  <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#0A2540]/95 backdrop-blur-md border-t border-white/10 px-4 py-2.5 flex items-center justify-between gap-3" data-testid="mobile-cta-bar">
    <a href="tel:0431469974" className="flex items-center gap-2 text-white text-sm font-medium">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
      0431 469 974
    </a>
    <Link to="/contact" className="px-5 py-1.5 rounded-full bg-white text-[#0A2540] text-sm font-semibold">
      Request a Quote
    </Link>
  </div>
);

const PublicLayout = ({ children }) => (
  <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
    <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
    <PublicNav />
    <main className="pt-[calc(3.5rem+1px)] xl:pt-[calc(3.5rem+1.75rem+2px)] pb-14 lg:pb-0">
      {children}
    </main>
    <PublicFooter />
    <MobileCTA />
  </div>
);

export default PublicLayout;
